import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, clamp01, envelope, inCubic, inOut, inQuad, keys, outBack, outCubic, outQuad, phase, settle, smooth, span, type Key } from '../curves.ts';
import { apply, timeline, type Ch, type KeyPose } from '../pose.ts';

/*
 * The dragon of the Burn (models/dragon.ts): `dragon` landed and `dragonAir` on the wing, two models with
 * the same 28 bones. Conventions (radians): it faces +Z; +X on the body, chest, neck and head pitches them
 * forward and down (−X rears up); +X on the jaw opens it; a thigh hangs down, so −X swings it forward;
 * +X on a front shin folds the foot back, −X on a hind shin folds it forward (both lift the foot). Wings are
 * authored for the right one (+X side) and mirrored: +Z raises it, +Y sweeps it back, +Y on the elbow and the
 * hand folds the wing. At rest the landed wings stand half-open; in flight they are at the top of the beat.
 *
 * Weight above all: a beast this size moves slowly, anticipates every big move and lets its neck and tail
 * trail the body (phase lags down the chains), so the ends whip and settle after the trunk has stopped.
 */

type Side = 'L' | 'R';
type DB =
  | 'root' | 'body' | 'chest' | 'hips' | 'neck1' | 'neck2' | 'neck3' | 'head' | 'jaw'
  | 'tail1' | 'tail2' | 'tail3' | 'tail4' | 'tail5'
  | `leg${'F' | 'B'}${Side}` | `shin${'F' | 'B'}${Side}`
  | `wing${Side}` | `elbow${Side}` | `hand${Side}`;
type B = Bones<DB>;
type P = KeyPose<DB>;

const NECK = ['neck1', 'neck2', 'neck3'] as const;
const TAIL = ['tail1', 'tail2', 'tail3', 'tail4', 'tail5'] as const;
const SIDES = [[1, 'R'], [-1, 'L']] as const;

/** The left twin of a right-side channel: rotations about Y and Z and the X offset flip. */
const mirror = (c: Ch): Ch => [c[0], -c[1], -c[2], -(c[3] ?? 0), c[4] ?? 0, c[5] ?? 0, c[6] ?? 1];

/** Both wings, authored for the right one: arm, forearm and hand. */
const wings = (arm: Ch, elbow: Ch = [0, 0, 0], hand: Ch = [0, 0, 0]): P => ({
  wingR: arm, elbowR: elbow, handR: hand, wingL: mirror(arm), elbowL: mirror(elbow), handL: mirror(hand),
});
/** All four legs: [thigh, shin] front and back, the same on both sides. */
const legs = (front: [Ch, Ch], back: [Ch, Ch] = [[0, 0, 0], [0, 0, 0]]): P => ({
  legFR: front[0], shinFR: front[1], legFL: mirror(front[0]), shinFL: mirror(front[1]),
  legBR: back[0], shinBR: back[1], legBL: mirror(back[0]), shinBL: mirror(back[1]),
});
/** A neck curl: the same pitch shared down the chain (heavier at the base), the head on its own. */
const neck = (x: number, head: number, y = 0): P => ({ neck1: [x * 0.45, y * 0.4, 0], neck2: [x * 0.33, y * 0.33, 0], neck3: [x * 0.22, y * 0.27, 0], head: [head, 0, 0] });
/** A tail curl down the chain, growing toward the tip: +x lifts it (it points back), +y swings it to the left (−X). */
const tail = (x: number, y: number): P => {
  const p: P = {};
  TAIL.forEach((n, i) => { p[n] = [x * (0.6 + i * 0.2), y * (0.6 + i * 0.2), 0]; });
  return p;
};
const sum = (...ps: P[]): P => {
  const out: Partial<Record<DB, number[]>> = {};
  for (const p of ps) {
    for (const [k, c] of Object.entries(p) as [DB, Ch][]) {
      const cur = out[k] ?? [0, 0, 0, 0, 0, 0, 1];
      for (let i = 0; i < 7; i++) {
        const v = c[i] ?? (i === 6 ? 1 : 0);
        cur[i] = i === 6 ? (cur[i] as number) * v : (cur[i] as number) + v;
      }
      out[k] = cur;
    }
  }
  return out as P;
};
/** Lags that make a move travel down the chains: neck to head, tail to tip, arm to hand. */
const CHAIN: Partial<Record<DB, number>> = {
  neck2: 0.03, neck3: 0.06, head: 0.08, jaw: 0.02,
  tail1: 0.05, tail2: 0.1, tail3: 0.15, tail4: 0.2, tail5: 0.25,
  elbowL: 0.04, elbowR: 0.04, handL: 0.08, handR: 0.08,
};
const lagged = (extra: Partial<Record<DB, number>> = {}): Partial<Record<DB, number>> => ({ ...CHAIN, ...extra });

/** A soft pulse around `c` (loop phase), for twitches. */
const pulse = (p: number, c: number, w = 0.04): number => {
  let d = p - c;
  d -= Math.round(d);
  return Math.exp(-((d / w) ** 2));
};

/** A shiver: fast and small, for strain (breath, roar). */
const shiver = (t: number, f = 13, off = 0): number => Math.sin(TAU * (f * t + off)) * 0.6 + Math.sin(TAU * (f * 1.7 * t + off * 2)) * 0.4;

/* ================================================================ landed ================================================================ */

/** Landed, the wings come in close along the back (rest is half-open). */
const FOLD: P = wings([0.05, 0.55, -0.08], [0, 0.45, 0], [0, 0.35, 0]);

/** Deep, slow breathing; the neck sways and looks about, the tail sweeps, the folded wings twitch. */
const idle = (): Clip<DB> => {
  const D = 4.8;
  return clip<DB>('idle', D, true, (b, t) => {
    const p = phase(t, D);
    const br = Math.sin(TAU * p);
    apply(b, FOLD);
    // Breathing: the chest lifts on the in-breath, the whole trunk rises a little.
    b.body.pos(0, 0.035 * br, 0).rot(0, 0, 0.012 * Math.sin(TAU * p + 1));
    b.chest.rot(-0.03 * br);
    b.hips.rot(0.01 * br);
    // It looks one way, holds, looks the other: a slow survey of the field.
    const LOOK: Key[] = [[0, 0], [0.12, 0.32], [0.38, 0.32], [0.55, -0.26], [0.82, -0.26], [1, 0]];
    const look = keys(p, LOOK);
    const sway = Math.sin(TAU * p - 0.4);
    b.neck1.rot(-0.04 * br, 0.1 * look + 0.04 * sway);
    b.neck2.rot(-0.02 * Math.sin(TAU * p - 0.5), 0.12 * look + 0.03 * Math.sin(TAU * p - 0.9));
    b.neck3.rot(0.01 * br, 0.14 * keys(phase(p - 0.03, 1), LOOK));
    b.head.rot(0.05 * br, 0.2 * look, -0.06 * look);
    // A smoking breath out: the jaw parts at the bottom of the breath; a snort mid-cycle.
    b.jaw.rot(0.1 * Math.max(0, -br) ** 2 + 0.12 * pulse(p, 0.62, 0.02));
    // The tail sweeps slowly, the motion running down it.
    TAIL.forEach((n, i) => b[n].rot(0.015 * Math.sin(TAU * p - i * 0.6), (0.05 + i * 0.025) * Math.sin(TAU * p - 0.8 - i * 0.7)));
    // The folded wings rise with the breath and now and then twitch.
    for (const [k, s] of SIDES) {
      const tw = pulse(p, k > 0 ? 0.3 : 0.74, 0.025);
      b[`wing${s}`].rot(0, 0, k * (0.04 * Math.sin(TAU * p - 0.3) + 0.06 * tw));
      b[`elbow${s}`].rot(0, -k * 0.06 * pulse(p, k > 0 ? 0.32 : 0.76, 0.03));
      b[`hand${s}`].rot(0, -k * 0.14 * pulse(p, k > 0 ? 0.33 : 0.77, 0.035), k * 0.05 * tw);
    }
    // Weight rocks from one forefoot to the other.
    b.legFL.rot(0.015 * br);
    b.legFR.rot(-0.015 * br);
  }, (t) => {
    const br = Math.sin(TAU * phase(t, D));
    return [1 + 0.012 * br, 1 + 0.006 * br, 1];
  });
};

/** One leg of the walk over its own cycle: stance (the foot drives back) then swing (lifts and reaches). */
const stride = (u: number, reach: number): { a: number; lift: number } => {
  const STANCE = 0.66;
  if (u < STANCE) return { a: -reach + 2 * reach * (u / STANCE), lift: 0 };
  const s = (u - STANCE) / (1 - STANCE);
  return { a: reach - 2 * reach * smooth(s), lift: Math.sin(Math.PI * s) };
};

/**
 * A massive four-legged walk (lateral sequence: hind left, fore left, hind right, fore right a quarter apart):
 * the weight drops onto each forefoot as it plants, the shoulders and hips roll against each other, the neck
 * counters the shoulders so the head stays on its prey and the tail swings an S behind.
 */
const walk = (D = 2.0): Clip<DB> =>
  clip<DB>('walk', D, true, (b, t) => {
    const p = phase(t, D);
    apply(b, FOLD);
    const LEGS = [['legBL', 'shinBL', 0, false], ['legFL', 'shinFL', 0.25, true], ['legBR', 'shinBR', 0.5, false], ['legFR', 'shinFR', 0.75, true]] as const;
    for (const [leg, shin, off, fore] of LEGS) {
      const { a, lift } = stride(phase(p - off, 1), fore ? 0.3 : 0.26);
      b[leg].rot(a - lift * (fore ? 0.18 : 0.12));
      b[shin].rot((fore ? 0.85 : -0.75) * lift);
    }
    // The trunk drops as each forefoot takes the weight (twice a cycle) and rolls toward the planted side.
    const drop = Math.cos(TAU * 2 * (p - 0.3));
    b.body.pos(0, -0.05 * drop - 0.02, 0.03 * Math.cos(TAU * 2 * (p - 0.1))).rot(0.012 * drop, 0, 0.035 * Math.sin(TAU * (p - 0.6)));
    const shoulders = -0.07 * Math.sin(TAU * (p - 0.34));
    b.chest.rot(0.015 * Math.cos(TAU * 2 * (p - 0.35)), shoulders, -0.02 * Math.sin(TAU * (p - 0.4)));
    b.hips.rot(-0.01 * drop, -0.06 * Math.sin(TAU * (p - 0.09)), 0.025 * Math.sin(TAU * (p - 0.15)));
    // The neck counters the shoulders (trailing a little) and bobs after the weight; the head holds steady.
    const n1 = 0.056 * Math.sin(TAU * (p - 0.38)), n2 = 0.035 * Math.sin(TAU * (p - 0.46));
    b.neck1.rot(0.035 * Math.cos(TAU * 2 * (p - 0.38)), n1);
    b.neck2.rot(0.025 * Math.cos(TAU * 2 * (p - 0.44)), n2);
    b.neck3.rot(-0.02 * Math.cos(TAU * 2 * (p - 0.5)), 0.02 * Math.sin(TAU * (p - 0.55)));
    b.head.rot(-0.04 * Math.cos(TAU * 2 * (p - 0.5)), -(shoulders + n1 + n2) * 0.6);
    b.jaw.rot(0.05 * Math.max(0, Math.sin(TAU * 2 * (p - 0.35))) ** 3);
    // The tail swings an S, the wave travelling to the tip.
    TAIL.forEach((n, i) => b[n].rot(0.02 * Math.cos(TAU * 2 * (p - 0.4 - i * 0.05)), (0.06 + i * 0.022) * Math.sin(TAU * (p - 0.15 - i * 0.12))));
    // Folded wings jolt with each step.
    for (const [k, s] of SIDES) {
      b[`wing${s}`].rot(0, 0, k * 0.035 * Math.cos(TAU * 2 * (p - 0.38)));
      b[`hand${s}`].rot(0, 0, k * 0.05 * Math.cos(TAU * 2 * (p - 0.45)));
    }
  }, (t) => {
    const c = Math.max(0, Math.cos(TAU * 2 * (phase(t, D) - 0.3)));
    return [1 + 0.008 * c, 1 - 0.01 * c, 1];
  });

/** The bite: a short rear back, jaws snapping shut at 0.15 s as it lunges (the sim deals it then), a tearing shake, a heavy settle. */
const attack = (): Clip<DB> => {
  const coil: P = sum(FOLD, neck(-0.55, -0.4), {
    body: [0, 0, 0, 0, 0.03, -0.1], chest: [-0.1, 0, 0], jaw: [0.75, 0, 0],
  }, wings([0, -0.05, 0.12]), tail(-0.03, 0));
  const snap: P = sum(FOLD, neck(0.95, 0.32), {
    body: [0.03, 0, 0, 0, -0.05, 0.22], chest: [0.12, 0, 0], hips: [-0.04, 0, 0], jaw: [-0.08, 0, 0],
  }, legs([[-0.18, 0, 0], [0.1, 0, 0]], [[0.12, 0, 0], [0, 0, 0]]), wings([0, 0.05, -0.08]), tail(0.06, 0));
  const tearA: P = sum(snap, { head: [0.38, 0.1, 0.12], neck3: [0.25, 0.08, 0] });
  const tearB: P = sum(snap, { head: [0.3, -0.12, -0.1], neck3: [0.2, -0.06, 0], body: [0, 0, 0, 0, -0.02, -0.04] });
  return clip<DB>('attack', 0.95, false, (b, t) => {
    timeline(b, t, [
      [0, FOLD],
      [0.08, coil, outQuad],
      [0.15, snap, inCubic],
      [0.3, tearA, outQuad],
      [0.45, tearB, inOut],
      [0.95, FOLD, inOut],
    ], { ...lagged(), neck2: 0.008, neck3: 0.014, head: 0.018, jaw: 0 });
  }, (t) => {
    const k = Math.max(0, settle(t - 0.15, 2.5, 7));
    return [1 + 0.02 * k, 1 - 0.025 * k, 1 + 0.01 * k];
  });
};

/**
 * Fire breath: it rears and fills its chest, the neck drawing back like a bow; then thrusts forward and down
 * with the jaws wide and holds, the neck sweeping the line of fire, trembling with the strain; then lets go.
 */
const breath = (): Clip<DB> => {
  const draw: P = sum(neck(-0.5, -0.22), {
    body: [0, 0, 0, 0, 0.08, -0.1], chest: [-0.18, 0, 0, 0, 0, 0, 1.04], hips: [0.06, 0, 0], jaw: [0.12, 0, 0],
  }, legs([[-0.38, 0, 0], [0.55, 0, 0]], [[0.1, 0, 0], [-0.08, 0, 0]]), wings([0, -0.12, 0.28], [0, -0.15, 0], [0, -0.12, 0]), tail(-0.04, 0));
  const thrust: P = sum(FOLD, neck(0.75, 0.22), {
    body: [0.02, 0, 0, 0, -0.03, 0.14], chest: [0.12, 0, 0, 0, 0, 0, 0.98], jaw: [0.85, 0, 0],
  }, legs([[-0.1, 0, 0], [0.06, 0, 0]], [[0.08, 0, 0], [0, 0, 0]]), wings([0, -0.08, 0.1]), tail(0.04, 0));
  return clip<DB>('breath', 1.6, false, (b, t) => {
    timeline(b, t, [
      [0, FOLD],
      [0.42, draw, inOut],
      [0.56, thrust, inCubic],
      [1.3, sum(thrust, { jaw: [0.05, 0, 0] }), linearHold],
      [1.6, FOLD, inOut],
    ], lagged({ neck2: 0.01, neck3: 0.02, head: 0.03, jaw: 0 }));
    // While it breathes: the neck sweeps the line of fire across, and everything strains.
    const hold = envelope(t, 0.52, 0.62, 1.25, 1.45);
    const sweep = keys(t, [[0.56, -0.16], [1.3, 0.16, inOut]]);
    b.neck1.rot(0, sweep * hold * 0.5);
    b.neck2.rot(0, sweep * hold * 0.4);
    b.head.rot(0.015 * shiver(t) * hold, -sweep * hold * 0.25, 0.01 * shiver(t, 9) * hold);
    b.neck3.rot(0.012 * shiver(t, 11, 0.3) * hold);
    b.jaw.rot(0.04 * shiver(t, 15) * hold);
    b.chest.rot(0.008 * shiver(t, 7) * hold);
  }, (t) => {
    const k = envelope(t, 0.1, 0.42, 0.45, 0.6);
    return [1 + 0.03 * k, 1 + 0.015 * k, 1];
  }, 'Aliento de fuego');
};
/** Ease for a hold (barely moving). */
const linearHold = (k: number): number => k;

/** The roar: head drops and gathers, then it rears with the jaws to the sky, wings flung wide, and shakes the air. */
const roar = (): Clip<DB> => {
  const gather: P = sum(FOLD, neck(0.35, 0.3), { body: [0, 0, 0, 0, -0.06, 0], chest: [0.06, 0, 0], jaw: [0.1, 0, 0] }, wings([0, 0.38, -0.18], [0, 0.32, 0], [0, 0.22, 0]), tail(-0.03, 0));
  const rear: P = sum(neck(-0.95, -0.55), {
    body: [0, 0, 0, 0, 0.14, -0.08], chest: [-0.3, 0, 0, 0, 0, 0, 1.03], hips: [0.08, 0, 0], jaw: [0.95, 0, 0],
  }, legs([[-0.42, 0, 0], [0.6, 0, 0]], [[0.12, 0, 0], [-0.1, 0, 0]]), wings([0, -0.3, 0.42], [0, -0.28, 0], [0, -0.22, 0]), tail(0.1, 0));
  return clip<DB>('roar', 1.7, false, (b, t) => {
    timeline(b, t, [
      [0, FOLD],
      [0.32, gather, inOut],
      [0.58, rear, outCubic],
      [1.2, sum(rear, neck(-0.08, -0.06), { jaw: [0.05, 0, 0] }), linearHold],
      [1.7, FOLD, inOut],
    ], lagged({ head: 0.05, jaw: 0.03 }));
    const k = envelope(t, 0.55, 0.65, 1.15, 1.4);
    b.head.rot(0.03 * shiver(t, 12) * k, 0.025 * shiver(t, 8, 0.2) * k);
    b.jaw.rot(0.06 * shiver(t, 17) * k);
    b.neck3.rot(0.015 * shiver(t, 10, 0.5) * k);
    for (const [kk, s] of SIDES) b[`hand${s}`].rot(0, 0, kk * 0.05 * shiver(t, 9, kk) * k);
  }, (t) => {
    const k = envelope(t, 0.3, 0.6, 1.15, 1.5);
    return [1 + 0.02 * k, 1 + 0.02 * k, 1];
  }, 'Rugido');
};

/** Take-off: it crouches with the wings unfurling high, then a mighty downbeat hurls it up; a second beat climbs. */
const takeoff = (): Clip<DB> => {
  const crouch: P = sum(neck(0.3, -0.1), {
    root: [0, 0, 0, 0, -0.18, 0], chest: [0.08, 0, 0], jaw: [0.2, 0, 0],
  }, legs([[-0.4, 0, 0], [0.75, 0, 0]], [[0.38, 0, 0], [-0.6, 0, 0]]), wings([0, -0.2, 0.48], [0, -0.32, 0], [0, -0.22, 0]), tail(0.06, 0));
  const leap: P = sum(neck(-0.35, 0.2), {
    root: [0, 0, 0, 0, 0.5, 0.12], body: [-0.12, 0, 0], jaw: [0.35, 0, 0],
  }, legs([[0.35, 0, 0], [0.3, 0, 0]], [[0.5, 0, 0], [0.1, 0, 0]]), wings([0, 0.08, -0.78], [0, -0.1, -0.15], [0, 0, 0.3]), tail(-0.1, 0));
  const climb: P = sum(neck(-0.25, 0.15), {
    root: [0, 0, 0, 0, 0.8, 0.2], body: [-0.08, 0, 0], jaw: [0.1, 0, 0],
  }, legs([[0.75, 0, 0], [-0.4, 0, 0]], [[0.75, 0, 0], [-0.7, 0, 0]]), wings([0, -0.05, 0.35], [0, 0.35, 0], [0, 0.3, 0]), tail(-0.05, 0));
  const beat2: P = sum(climb, { root: [0, 0, 0, 0, 0.3, 0.1] }, wings([0, 0.05, -0.55], [0, -0.05, -0.1], [0, 0, 0.25]));
  return clip<DB>('takeoff', 1.25, false, (b, t) => {
    timeline(b, t, [
      [0, FOLD],
      [0.38, crouch, inOut],
      [0.58, leap, inCubic],
      [0.92, climb, inOut],
      [1.25, beat2, inCubic],
    ], lagged({ root: 0, body: 0, legFL: 0.04, legFR: 0.04, legBL: 0.06, legBR: 0.06, shinFL: 0.07, shinFR: 0.07, shinBL: 0.09, shinBR: 0.09 }));
  }, (t) => {
    const c = envelope(t, 0.2, 0.38, 0.4, 0.5);
    const s = envelope(t, 0.45, 0.58, 0.6, 0.8);
    return [1 + 0.02 * c - 0.01 * s, 1 - 0.03 * c + 0.03 * s, 1];
  }, 'Despegue');
};

/** Struck: it flinches back, the neck whipping and settling, the wings jerking open. */
const hit = (): Clip<DB> =>
  clip<DB>('hit', 0.45, false, (b, t) => {
    apply(b, FOLD);
    const k = settle(t, 2.2, 7) * span(t, 0, 0.04, outQuad);
    const k2 = settle(t - 0.05, 2.2, 7) * span(t, 0.05, 0.09, outQuad);
    b.body.pos(0, 0, -0.08 * k).rot(-0.03 * k, 0.04 * k);
    b.chest.rot(-0.1 * k, 0.05 * k);
    b.neck1.rot(-0.14 * k, 0.06 * k);
    b.neck2.rot(-0.12 * k2, 0.08 * k2);
    b.neck3.rot(-0.08 * k2);
    b.head.rot(-0.25 * k2, 0.18 * k2, 0.1 * k2);
    b.jaw.rot(0.4 * Math.abs(k));
    for (const [kk, s] of SIDES) {
      b[`wing${s}`].rot(0, -kk * 0.08 * k, kk * 0.14 * k);
      b[`hand${s}`].rot(0, -kk * 0.12 * k2, kk * 0.1 * k2);
    }
    TAIL.forEach((n, i) => b[n].rot(0, 0.06 * settle(t - 0.04 * i, 2, 6) * span(t, 0.04 * i, 0.04 * i + 0.05)));
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.03 * k, 1 - 0.035 * k, 1 + 0.02 * k];
  });

/** Stunned: head hanging and lolling, wings drooped, it sways on its feet. */
const stun = (): Clip<DB> => {
  const D = 2.4;
  const slump: P = sum(neck(0.55, 0.25), { body: [0, 0, 0, 0, -0.06, 0], jaw: [0.32, 0, 0] }, wings([0, 0.15, -0.38], [0, 0.1, -0.12], [0, 0.05, -0.15]), tail(0.02, 0));
  return clip<DB>('stun', D, true, (b, t) => {
    const p = phase(t, D);
    apply(b, slump);
    b.body.rot(0.02 * Math.sin(TAU * p * 2), 0, 0.05 * Math.sin(TAU * p));
    b.neck1.rot(0.04 * Math.sin(TAU * p * 2 - 0.5), 0.12 * Math.sin(TAU * p - 0.4));
    b.neck2.rot(0, 0.12 * Math.sin(TAU * p - 0.9));
    b.neck3.rot(0.05 * Math.sin(TAU * p * 2 - 1.3), 0.1 * Math.sin(TAU * p - 1.4));
    b.head.rot(0.08 * Math.sin(TAU * p + 0.8), 0.12 * Math.cos(TAU * p - 1.6), 0.22 * Math.sin(TAU * p - 1.8));
    b.jaw.rot(0.06 * Math.sin(TAU * p * 2));
    for (const [k, s] of SIDES) b[`hand${s}`].rot(0, 0, k * 0.05 * Math.sin(TAU * p + k));
    TAIL.forEach((n, i) => b[n].rot(0, 0.05 * Math.sin(TAU * p - 0.5 - i * 0.5)));
  });
};

/** Death: it rears in agony with the wings flailing, topples and crashes onto its side; the neck comes down last. */
const die = (): Clip<DB> => {
  const agony: P = sum(neck(-0.9, -0.55), {
    body: [0, 0, 0, 0, 0.12, -0.06], chest: [-0.3, 0, 0], jaw: [0.95, 0, 0],
  }, legs([[-0.45, 0, 0], [0.6, 0, 0]], [[0.1, 0, 0], [-0.1, 0, 0]]), wings([0, -0.25, 0.45], [0, -0.25, 0], [0, -0.2, 0]), tail(0.1, 0.1));
  const topple: P = sum(neck(-0.4, -0.3, 0.2), {
    body: [0.05, 0.1, 0.75, -0.25, -0.15, 0], chest: [-0.05, 0, 0.08], jaw: [0.7, 0, 0],
  }, legs([[-0.2, 0, -0.2], [0.3, 0, 0]], [[0.15, 0, -0.15], [-0.2, 0, 0]]), {
    wingR: [0, -0.2, 0.55], elbowR: [0, -0.2, 0], handR: [0, -0.1, 0],
    wingL: [0, -0.2, -0.3], elbowL: [0, -0.2, 0], handL: [0, -0.1, 0],
  }, tail(0, 0.2));
  const down: P = sum(neck(0.25, 0.15, 0.1), {
    body: [0.06, 0.12, 1.32, -0.35, -0.5, 0], chest: [0, 0, 0.1], hips: [0, 0, -0.08],
    neck1: [0, 0, 0.12], neck2: [0, 0, 0.12], neck3: [0, 0, 0.08], head: [0, 0, 0.25], jaw: [0.45, 0, 0],
  }, {
    legFR: [-0.35, 0, -0.25], shinFR: [0.35, 0, 0], legBR: [0.1, 0, -0.3], shinBR: [-0.25, 0, 0],
    legFL: [-0.15, 0, 0.35], shinFL: [0.5, 0, 0], legBL: [0.2, 0, 0.35], shinBL: [-0.35, 0, 0],
    wingR: [0, 0.4, -1.35], elbowR: [0, 0.25, -0.2], handR: [0, 0.15, -0.25],
    wingL: [0, -0.3, -0.6], elbowL: [0, -0.25, 0], handL: [0, -0.15, 0],
  }, tail(0.04, 0.22));
  const bounce: P = sum(down, { body: [0, 0, -0.06, 0, 0.06, 0] });
  return clip<DB>('die', 2.5, false, (b, t) => {
    timeline(b, t, [
      [0, FOLD],
      [0.45, agony, outCubic],
      [0.95, topple, inOut],
      [1.35, down, inQuad],
      [1.5, bounce, outQuad],
      [1.75, down, inOut],
    ], lagged({ neck1: 0.15, neck2: 0.3, neck3: 0.4, head: 0.5, jaw: 0.45, wingL: 0.05, wingR: 0.08, handL: 0.18, handR: 0.2 }));
    // The wings beat wildly while it rears, and the jaws gape and shudder.
    const fl = envelope(t, 0.15, 0.3, 0.85, 1.15);
    for (const [k, s] of SIDES) {
      b[`wing${s}`].rot(0, 0, k * 0.28 * Math.sin(TAU * (2.6 * t + (k > 0 ? 0 : 0.15))) * fl);
      b[`hand${s}`].rot(0, 0, k * 0.2 * Math.sin(TAU * (2.6 * t - 0.12 + (k > 0 ? 0 : 0.15))) * fl);
    }
    b.head.rot(0.04 * shiver(t, 11) * fl);
    b.jaw.rot(0.06 * shiver(t, 16) * fl);
  }, (t) => {
    const k = Math.max(0, settle(t - 1.35, 2.5, 6)) * (t > 1.35 ? 1 : 0);
    return [1 + 0.02 * k, 1 - 0.03 * k, 1];
  });
};

/** A tail sweep around it (the sim's `dragonSweep`): it turns, the tail lashing a wide arc and whipping back. */
const sweep = (): Clip<DB> => {
  // At rest the tail curls round to its right: it winds tighter, then uncoils across to the left.
  const wind: P = sum(FOLD, neck(0.1, 0, -0.35), { body: [0, -0.12, 0, 0, -0.04, 0], hips: [0, -0.18, 0] }, tail(0.03, -0.12));
  const lash: P = sum(FOLD, neck(0, 0.05, 0.45), { body: [0, 0.2, 0.03, 0, -0.03, 0], hips: [0, 0.3, 0], jaw: [0.3, 0, 0] }, tail(0.06, 0.42), wings([0, -0.1, 0.12]));
  return clip<DB>('sweep', 1.3, false, (b, t) => {
    timeline(b, t, [
      [0, FOLD],
      [0.35, wind, inOut],
      [0.62, lash, inCubic],
      [0.8, sum(lash, tail(0, 0.08)), outQuad],
      [1.3, FOLD, inOut],
    ], lagged({ tail1: 0.02, tail2: 0.05, tail3: 0.08, tail4: 0.11, tail5: 0.14 }));
  }, undefined, 'Coletazo');
};

/** Devouring a spectre: it lunges low, the jaws close on it, and it throws the head back to swallow. */
const devour = (): Clip<DB> => {
  const lunge: P = sum(FOLD, neck(1.1, 0.35), { body: [0.04, 0, 0, 0, -0.08, 0.25], chest: [0.15, 0, 0], jaw: [0.85, 0, 0] }, legs([[-0.2, 0, 0], [0.15, 0, 0]], [[0.15, 0, 0], [0, 0, 0]]), tail(-0.06, 0));
  const bite: P = sum(lunge, { jaw: [-0.08, 0, 0], head: [0.1, 0, 0] });
  const gulp: P = sum(FOLD, neck(-0.7, -0.5), { body: [0, 0, 0, 0, 0.05, -0.05], chest: [-0.12, 0, 0], jaw: [0.15, 0, 0] }, tail(-0.05, 0));
  return clip<DB>('devour', 1.5, false, (b, t) => {
    timeline(b, t, [
      [0, FOLD],
      [0.3, lunge, inCubic],
      [0.4, bite, outQuad],
      [0.8, gulp, inOut],
      [1.0, sum(gulp, { head: [-0.1, 0, 0], jaw: [-0.15, 0, 0] }), outQuad],
      [1.5, FOLD, inOut],
    ], lagged({ head: 0.03, jaw: 0 }));
  }, undefined, 'Devorar');
};

const landedClips = (): Clip[] => [idle(), walk(), attack(), breath(), roar(), takeoff(), hit(), stun(), die(), sweep(), devour()] as Clip[];

/* ================================================================ on the wing ================================================================ */

/** The shoulder over one beat (0 is the top, where the wings rest): a hard, quick downstroke and a slower recovery. */
const FLAP: Key[] = [[0, 0], [0.42, -1.12, inOut], [1, 0, inOut]];
const flap = (u: number): number => keys(phase(u, 1), FLAP);

/**
 * Flight: `beats` wing beats over the loop. The body rises on the downstroke and sinks on the recovery; the
 * forearm and hand trail the arm (the tips flex up as it drives down) and the wing half folds on the way up;
 * the neck and tail ride the beat with a lag down each chain, and drift side to side once a loop.
 */
const fly = (b: B, t: number, D: number, beats: number, amp = 1): void => {
  const p = phase(t, D);
  const u = phase(p * beats, 1);
  for (const [k, s] of SIDES) {
    const a = flap(u) * amp;
    const e = flap(u - 0.06) * amp;
    const h = flap(u - 0.13) * amp;
    const up = u > 0.42 ? Math.sin((Math.PI * (u - 0.42)) / 0.58) : 0;
    const upH = u > 0.47 ? Math.sin((Math.PI * (u - 0.47)) / 0.53) : 0;
    const fwd = u < 0.42 ? Math.sin((Math.PI * u) / 0.42) : 0;
    b[`wing${s}`].rot(0.1 * fwd, k * (-0.14 * fwd + 0.06 * up), k * a);
    b[`elbow${s}`].rot(0, k * 0.32 * up, k * (e - a) * 0.7);
    b[`hand${s}`].rot(0, k * 0.3 * upH, k * (h - e) * 0.8);
  }
  b.root.pos(0, 0.13 * amp * Math.cos(TAU * (u - 0.52)), 0);
  b.body.rot(-0.04 * amp * Math.sin(TAU * (u - 0.1)), 0, 0.02 * Math.sin(TAU * p));
  NECK.forEach((n, i) => b[n].rot(0.05 * amp * Math.sin(TAU * (u - 0.2 - 0.07 * i)), (0.06 - i * 0.01) * Math.sin(TAU * (p - 0.05 * i))));
  b.head.rot(-0.1 * amp * Math.sin(TAU * (u - 0.3)), -0.08 * Math.sin(TAU * (p - 0.2)));
  b.jaw.rot(0.06 * Math.max(0, Math.sin(TAU * p)) ** 2);
  TAIL.forEach((n, i) => b[n].rot((0.05 + i * 0.015) * amp * Math.sin(TAU * (u - 0.3 - 0.09 * i)), (0.04 + i * 0.015) * Math.sin(TAU * (p - 0.15 - 0.1 * i))));
  // The tucked legs swing a little with the beat.
  for (const s of ['L', 'R'] as const) {
    b[`legF${s}`].rot(0.06 * Math.sin(TAU * (u - 0.35)));
    b[`legB${s}`].rot(0.08 * Math.sin(TAU * (u - 0.4)));
  }
};

const airIdle = (): Clip<DB> => clip<DB>('idle', 2.6, true, (b, t) => fly(b, t, 2.6, 2));
const airWalk = (): Clip<DB> => clip<DB>('walk', 2.1, true, (b, t) => fly(b, t, 2.1, 2, 1.08));

/** Wings held out flat, riding the air: small corrections of the wings, a slow bank, the tail steering. */
const SPREAD: P = wings([-0.05, -0.04, -0.55], [0, -0.06, 0.05], [0, -0.04, 0.06]);
const glide = (): Clip<DB> => {
  const D = 4.0;
  return clip<DB>('glide', D, true, (b, t) => {
    const p = phase(t, D);
    apply(b, SPREAD);
    const bank = Math.sin(TAU * p);
    b.root.pos(0, 0.05 * Math.sin(TAU * p * 2 - 0.5), 0);
    b.body.rot(0.02 * Math.sin(TAU * p * 2), 0.02 * bank, 0.06 * bank);
    for (const [k, s] of SIDES) {
      b[`wing${s}`].rot(0.04 * Math.sin(TAU * p * 2 + k), 0, k * (0.05 * Math.sin(TAU * p * 2 + (k > 0 ? 0 : 0.5)) - 0.04 * k * bank));
      b[`hand${s}`].rot(0, 0, k * 0.06 * Math.sin(TAU * p * 2 - 0.6 + (k > 0 ? 0 : 0.5)));
    }
    NECK.forEach((n, i) => b[n].rot(0.02 * Math.sin(TAU * (p * 2 - 0.1 * i)), -0.05 * Math.sin(TAU * (p - 0.06 * i))));
    b.head.rot(-0.03 * Math.sin(TAU * p * 2), 0.1 * Math.sin(TAU * (p - 0.25)));
    TAIL.forEach((n, i) => b[n].rot(0.02 * Math.sin(TAU * (p * 2 - 0.1 * i)), (0.05 + 0.02 * i) * Math.sin(TAU * (p - 0.1 - 0.08 * i))));
  }, undefined, 'Planeo');
};

/** Strafing fire from the air: it pulls up and draws the neck back, then pitches over and pours fire down, wings spread to hold the line. */
const airBreath = (): Clip<DB> => {
  const draw: P = sum(neck(-0.6, -0.25), { root: [0, 0, 0, 0, 0.18, 0], body: [-0.22, 0, 0], chest: [0, 0, 0, 0, 0, 0, 1.04], jaw: [0.1, 0, 0] }, wings([0, -0.05, 0.12], [0, -0.1, 0], [0, -0.05, 0]), tail(0.1, 0));
  const aim: P = sum(neck(0.7, 0.35), { root: [0, 0, 0, 0, 0.08, 0], body: [0.2, 0, 0], jaw: [0.85, 0, 0] }, tail(-0.08, 0));
  // One hard downbeat to hang in the air, then the wings held out while the fire pours.
  const pour: P = sum(aim, wings([0, 0.06, -0.95], [0, 0, -0.12], [0, 0, 0.2]));
  const hold: P = sum(aim, SPREAD, wings([0, 0.04, 0.1]));
  return clip<DB>('breath', 1.6, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.4, draw, inOut],
      [0.55, pour, inCubic],
      [0.75, hold, inOut],
      [1.3, sum(hold, { jaw: [-0.1, 0, 0] }), linearHold],
      [1.6, {}, inOut],
    ], lagged({ neck2: 0.01, neck3: 0.02, head: 0.03, jaw: 0 }));
    const h = envelope(t, 0.52, 0.62, 1.25, 1.45);
    const sw = keys(t, [[0.55, -0.18], [1.3, 0.18, inOut]]);
    b.neck1.rot(0, sw * h * 0.5);
    b.neck2.rot(0, sw * h * 0.4);
    b.head.rot(0.015 * shiver(t) * h, -sw * h * 0.2);
    b.jaw.rot(0.04 * shiver(t, 15) * h);
    for (const [k, s] of SIDES) b[`hand${s}`].rot(0, 0, k * 0.05 * Math.sin(TAU * 3 * t + (k > 0 ? 0 : 0.4)) * h);
  }, undefined, 'Aliento de fuego');
};

/** Landing: it flares, wings thrown forward to brake, legs reaching down; back-beats, touches down and settles its weight. */
const land = (): Clip<DB> => {
  const flare: P = sum(neck(-0.5, 0.25), {
    root: [0, 0, 0, 0, -0.25, 0], body: [-0.42, 0, 0], jaw: [0.15, 0, 0],
  }, legs([[-0.55, 0, 0], [-0.35, 0, 0]], [[-0.35, 0, 0], [0.45, 0, 0]]), wings([0.15, -0.45, -0.25], [0, -0.15, 0], [0, -0.1, 0.1]), tail(0.2, 0));
  const brake: P = sum(neck(-0.6, 0.3), {
    root: [0, 0, 0, 0, -0.65, 0], body: [-0.3, 0, 0], jaw: [0.1, 0, 0],
  }, legs([[-0.65, 0, 0], [-0.45, 0, 0]], [[-0.45, 0, 0], [0.55, 0, 0]]), wings([0.1, -0.3, -0.95], [0, -0.05, -0.15], [0, 0, 0.25]), tail(0.25, 0));
  const touch: P = sum(neck(-0.75, 0.35), {
    root: [0, 0, 0, 0, -1.0, 0], body: [-0.06, 0, 0, 0, -0.08, 0], jaw: [0.05, 0, 0],
  }, legs([[-0.75, 0, 0], [-0.5, 0, 0]], [[-0.5, 0, 0], [0.6, 0, 0]]), wings([0, -0.2, 0.1], [0, 0.1, 0], [0, 0.1, 0]), tail(0.12, 0));
  const settled: P = sum(touch, { body: [-0.1, 0, 0, 0, 0, 0] }, wings([-0.15, -0.75, 0.2], [0, 0.2, 0], [0, 0.15, 0]), neck(-0.05, 0));
  return clip<DB>('land', 1.25, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.38, flare, inOut],
      [0.68, brake, inCubic],
      [0.88, touch, inQuad],
      [1.25, settled, outBack],
    ], lagged({ root: 0, legFL: 0.02, legFR: 0.02, shinFL: 0.04, shinFR: 0.04 }));
  }, (t) => {
    const k = Math.max(0, settle(t - 0.88, 2.5, 7)) * (t > 0.88 ? 1 : 0);
    return [1 + 0.03 * k, 1 - 0.04 * k, 1 + 0.02 * k];
  }, 'Aterrizaje');
};

/** An aerial rake: it tucks and dives, swings the hind claws through, then beats hard to climb away. */
const airAttack = (): Clip<DB> => {
  const tuck: P = sum(neck(0.3, 0.2), { root: [0, 0, 0, 0, -0.25, 0.25], body: [0.3, 0, 0], jaw: [0.6, 0, 0] }, wings([0, 0.35, 0.2], [0, 0.4, 0], [0, 0.3, 0]), tail(-0.15, 0));
  const rake: P = sum(neck(0.5, 0.3), { root: [0, 0, 0, 0, -0.4, 0.5], body: [-0.15, 0, 0], jaw: [0.2, 0, 0] }, legs([[-0.7, 0, 0], [0.4, 0, 0]], [[-1.0, 0, 0], [0.5, 0, 0]]), wings([0, 0, -0.6], [0, 0.05, -0.1], [0, 0, 0.2]), tail(0.2, 0));
  const climb: P = sum(neck(-0.2, 0), { root: [0, 0, 0, 0, -0.05, 0.25], body: [-0.12, 0, 0] }, wings([0, 0.05, -1.0], [0, 0, -0.15], [0, 0, 0.25]), tail(0.08, 0));
  return clip<DB>('attack', 1.0, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.12, tuck, outQuad],
      [0.3, rake, inCubic],
      [0.55, climb, inOut],
      [1.0, {}, inOut],
    ], lagged());
  });
};

const airHit = (): Clip<DB> =>
  clip<DB>('hit', 0.5, false, (b, t) => {
    const k = settle(t, 2, 6.5) * span(t, 0, 0.04, outQuad);
    const k2 = settle(t - 0.06, 2, 6.5) * span(t, 0.06, 0.1, outQuad);
    b.root.pos(0, -0.3 * Math.abs(k), -0.1 * k);
    b.body.rot(-0.16 * k, 0.08 * k, 0.22 * k);
    b.neck1.rot(-0.25 * k, 0.1 * k);
    b.neck2.rot(-0.2 * k2, 0.14 * k2);
    b.head.rot(-0.35 * k2, 0.2 * k2, 0.15 * k2);
    b.jaw.rot(0.5 * Math.abs(k));
    for (const [kk, s] of SIDES) {
      b[`wing${s}`].rot(0, kk * 0.2 * k, kk * (kk > 0 ? -0.45 : 0.3) * k);
      b[`elbow${s}`].rot(0, kk * 0.25 * Math.abs(k2));
      b[`hand${s}`].rot(0, kk * 0.2 * Math.abs(k2), kk * 0.15 * k2);
    }
    TAIL.forEach((n, i) => b[n].rot(0.08 * settle(t - 0.04 * i, 2, 6) * span(t, 0.04 * i, 0.04 * i + 0.05)));
  });

/** Stunned in the air: a ragged, uneven beat, sagging and catching itself, head lolling. */
const airStun = (): Clip<DB> => {
  const D = 2.4;
  return clip<DB>('stun', D, true, (b, t) => {
    const p = phase(t, D);
    for (const [k, s] of SIDES) {
      const u = phase(p * 2 + (k > 0 ? 0 : 0.18), 1);
      const a = flap(u) * 0.7 - 0.15;
      b[`wing${s}`].rot(0, 0, k * a);
      b[`elbow${s}`].rot(0, k * 0.2, k * (flap(u - 0.08) * 0.7 - 0.15 - a) * 0.8);
      b[`hand${s}`].rot(0, k * 0.15, k * -0.15);
    }
    b.root.pos(0, -0.12 + 0.1 * Math.sin(TAU * p) + 0.05 * Math.cos(TAU * (p * 2 - 0.5)), 0);
    b.body.rot(0.1, 0, 0.1 * Math.sin(TAU * p));
    NECK.forEach((n, i) => b[n].rot(0.15 + 0.05 * Math.sin(TAU * (p * 2 - 0.1 * i)), 0.1 * Math.sin(TAU * (p - 0.08 * i))));
    b.head.rot(0.2 + 0.08 * Math.sin(TAU * p + 0.8), 0.1 * Math.cos(TAU * p - 1.6), 0.25 * Math.sin(TAU * p - 1.8));
    b.jaw.rot(0.3 + 0.05 * Math.sin(TAU * p * 2));
    TAIL.forEach((n, i) => b[n].rot(0.06 * Math.sin(TAU * (p * 2 - 0.1 * i)), 0.06 * Math.sin(TAU * (p - 0.1 * i))));
    for (const n of ['legFL', 'legFR', 'legBL', 'legBR'] as const) b[n].rot(0.25);
  });
};

/** Death on the wing: a convulsion, then the wings crumple and it falls out of the sky, rolling over, and hits the ground. */
const airDie = (): Clip<DB> => {
  const spasm: P = sum(neck(-0.8, -0.5), { root: [0, 0, 0, 0, 0.15, 0], body: [-0.25, 0, 0], jaw: [0.95, 0, 0] }, wings([0, -0.1, 0.25], [0, -0.15, 0], [0, -0.1, 0]), tail(-0.25, 0));
  const crumple: P = sum(neck(0.2, 0.2, 0.3), { root: [0, 0, 0, 0, -0.6, 0.2], body: [0.3, 0.1, 0.9], jaw: [0.6, 0, 0] }, {
    wingR: [0.3, 0.2, 0.25], elbowR: [0, 0.6, 0.2], handR: [0, 0.45, 0],
    wingL: [-0.2, -0.1, -0.1], elbowL: [0, -0.5, -0.1], handL: [0, -0.4, 0],
  }, legs([[0.4, 0, 0.2], [0.3, 0, 0]], [[0.5, 0, 0.2], [-0.3, 0, 0]]), tail(-0.2, 0.25));
  const impact: P = sum(neck(0.45, 0.2, 0.15), { root: [0, 0, 0, 0, -2.35, 0.4], body: [0.15, 0.15, 1.45, -0.3, 0, 0], jaw: [0.4, 0, 0] }, {
    wingR: [0.1, 0.5, -0.6], elbowR: [0, 0.3, 0], handR: [0, 0.2, -0.2],
    wingL: [0, -0.2, -0.3], elbowL: [0, -0.2, 0], handL: [0, -0.15, 0],
  }, legs([[0.25, 0, 0.3], [0.2, 0, 0]], [[0.3, 0, 0.3], [-0.2, 0, 0]]), tail(0.04, 0.3), { neck1: [0, 0, 0.15], neck2: [0, 0, 0.15], head: [0, 0, 0.3] });
  const rebound: P = sum(impact, { root: [0, 0, 0, 0, 0.1, 0], body: [0, 0, -0.06] });
  return clip<DB>('die', 2.5, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.3, spasm, outCubic],
      [1.0, crumple, inOut],
      [1.65, impact, inQuad],
      [1.8, rebound, outQuad],
      [2.05, impact, inOut],
    ], lagged({ root: 0, body: 0, neck1: 0.06, neck2: 0.14, neck3: 0.2, head: 0.28, jaw: 0.2 }));
    const fl = envelope(t, 0.1, 0.25, 0.7, 1.1);
    for (const [k, s] of SIDES) b[`hand${s}`].rot(0, 0, k * 0.25 * Math.sin(TAU * (3 * t + (k > 0 ? 0 : 0.2))) * fl);
    b.jaw.rot(0.05 * shiver(t, 15) * fl);
    // A slow tumble while it falls.
    b.body.rot(0.25 * Math.sin(Math.PI * clamp01((t - 0.5) / 1.15)));
  }, (t) => {
    const k = Math.max(0, settle(t - 1.65, 2.5, 6)) * (t > 1.65 ? 1 : 0);
    return [1 + 0.03 * k, 1 - 0.04 * k, 1];
  });
};

const airClips = (): Clip[] => [airIdle(), airWalk(), glide(), airBreath(), land(), airAttack(), airHit(), airStun(), airDie()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = {
  dragon: landedClips,
  dragonAir: airClips,
};
